import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  HelpCircle,
  LogOut,
  Moon,
  Palette,
  PanelLeft,
  Search,
  Settings,
  SlidersHorizontal,
  Sun,
  User,
} from 'lucide-react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { fetchCollegeData } from '../../helpers/collegeAPI';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import './Navbar.css';

// Keep the global search implementation ready for a later release.
const NAVBAR_SEARCH_ENABLED = false;

function Navbar({ onToggleSidebar, onToggleNotifications, onToggleDarkMode, darkMode }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSigningOut, setProfileSigningOut] = useState(false);
  const [collegeNames, setCollegeNames] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState('');
  const searchRef = useRef(null);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();

  // Fetch college data for autocomplete
  useEffect(() => {
    if (!NAVBAR_SEARCH_ENABLED) return undefined;
    fetchCollegeData()
      .then((data) => setCollegeNames(data))
      .catch((error) => console.error('Error fetching college data:', error));
    return undefined;
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setProfileOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Build autocomplete options
  const resultsArray = collegeNames && collegeNames.results
    ? Object.values(collegeNames.results)
    : [];
  const collegeNamesList = [
    ...new Set(resultsArray.map((r) => r['latest.school.name']).filter(Boolean)),
  ];

  const handleSchoolSelect = (event, newValue) => {
    setSelectedSchool(newValue || '');
    if (newValue) {
      setSearchOpen(false);
    }
  };

  const displayName = user?.displayName || 'My account';
  const email = user?.email || 'Signed in';
  const avatarInitial = (user?.displayName || user?.email || 'M').trim().charAt(0).toUpperCase();

  const handleProfileNavigate = (section) => {
    if (section === 'support') {
      navigate('/support');
      setProfileOpen(false);
      return;
    }

    navigate(`/account#${section}`);
    setProfileOpen(false);
  };

  const handleProfileSignOut = async () => {
    setProfileSigningOut(true);
    try {
      await logout();
      setProfileOpen(false);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      setProfileSigningOut(false);
    }
  };

  return (
    <nav className="navbar" id="main-navbar">
      {/* ── Left: Sidebar icon + title ── */}
      <div className="navbar__left">
        <div
          className="navbar__icon"
          aria-label="Toggle sidebar"
          data-tour="sidebar-toggle"
          onClick={onToggleSidebar}
        >
          <PanelLeft size={20} strokeWidth={1.5} />
        </div>
        <span className="navbar__title">Dashboards</span>
      </div>

      {/* Global search is intentionally hidden until its full search experience is ready. */}
      {NAVBAR_SEARCH_ENABLED && (
        <div className="navbar__search-wrapper" ref={searchRef}>
          <div
            className="navbar__search"
            id="navbar-search"
            onClick={() => setSearchOpen(true)}
          >
            <div className="navbar__icon" aria-label="Search">
              <Search size={14} strokeWidth={1.5} />
            </div>
            <span className="navbar__search-text">
              {selectedSchool || 'Search Colleges'}
            </span>
            <div className="navbar__icon-filter" aria-label="Filter">
              <SlidersHorizontal size={14} strokeWidth={1.5} />
            </div>
          </div>

          {/* Dropdown with autocomplete */}
          {searchOpen && (
            <div className="navbar__search-dropdown">
              <Autocomplete
                disablePortal
                options={collegeNamesList}
                autoFocus
                openOnFocus
                onChange={handleSchoolSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search for a college..."
                    autoFocus
                    size="small"
                  />
                )}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Right: Sun / Notification / Profile icons ── */}
      <div className="navbar__right">
        <div
          className="navbar__icon"
          aria-label="Toggle dark mode"
          onClick={onToggleDarkMode}
        >
          {darkMode ? <Moon size={20} strokeWidth={1.5} /> : <Sun size={20} strokeWidth={1.5} />}
        </div>
        <div
          className="navbar__icon navbar__icon--bell"
          aria-label="Notifications"
          onClick={onToggleNotifications}
        >
          <Bell size={20} strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span className="navbar__notif-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        <div className="navbar__profile-menu" ref={profileRef}>
          <button
            className="navbar__icon-profile-wrapper"
            aria-label="Profile"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen((prev) => !prev)}
          >
            <span>{avatarInitial}</span>
          </button>

          {profileOpen && (
            <div className="navbar__profile-dropdown">
              <div className="navbar__profile-header">
                <div className="navbar__profile-avatar">{avatarInitial}</div>
                <div className="navbar__profile-copy">
                  <strong>{displayName}</strong>
                  <span>{email}</span>
                </div>
              </div>

              <div className="navbar__profile-items">
                <button onClick={() => handleProfileNavigate('account')}>
                  <User size={15} strokeWidth={1.6} />
                  Account
                </button>
                <button onClick={() => handleProfileNavigate('settings')}>
                  <Settings size={15} strokeWidth={1.6} />
                  Settings
                </button>
                <button onClick={() => handleProfileNavigate('customization')}>
                  <Palette size={15} strokeWidth={1.6} />
                  Customization
                </button>
                <button onClick={() => handleProfileNavigate('support')}>
                  <HelpCircle size={15} strokeWidth={1.6} />
                  Support
                </button>
              </div>

              <div className="navbar__profile-divider" />

              <button
                className="navbar__profile-signout"
                onClick={handleProfileSignOut}
                disabled={profileSigningOut}
              >
                <LogOut size={15} strokeWidth={1.6} />
                {profileSigningOut ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
